import { useState } from 'react';

import { FileMessage } from '../../../../types/FileMessage';
import { SegmentedControl } from '../../../cdl/SegmentedControl/SegmentedControl';
import { translate } from '../../helpers/translate.helper';
import { useRole } from '../../hooks/useRole';
import { Box } from '../../ui/Box';
import { AttachmentsConfigContext } from '../hooks/AttachmentsConfigContext';
import { useUploadAreaReducer } from '../hooks/useUploadAreaReducer';
import { AttachmentListFile } from '../types/AttachmentListFile';
import { AttachmentsConfig } from '../types/AttachmentsConfig';

import { AttachmentList } from './AttachmentList';
import { UploadArea } from './UploadArea';

type FileVisibility = 'shared' | 'internal';

interface AttachmentsProps {
    files: AttachmentListFile[];
    internalFiles?: AttachmentListFile[];
    config: AttachmentsConfig;
    companyId?: string;
    counterCompanyName?: string;
}

export const Attachments = ({ files, internalFiles, companyId, counterCompanyName, config }: AttachmentsProps) => {
    const [selectedVisibility, setSelectedVisibility] = useState<FileVisibility>('shared');
    const [sharedUploadedFiles, sharedDispatch] = useUploadAreaReducer();
    const [internalUploadedFiles, internalDispatch] = useUploadAreaReducer();
    const role = useRole();

    const isInternalView = selectedVisibility === 'internal';
    const currentFiles = isInternalView ? internalFiles || [] : files;
    const uploadedFiles = isInternalView ? internalUploadedFiles : sharedUploadedFiles;
    const dispatch = isInternalView ? internalDispatch : sharedDispatch;

    const sharedCount = files.length;
    const internalCount = internalFiles?.length || 0;
    const hasAnyFiles = sharedCount > 0 || internalCount > 0;
    const shouldShowSegmentedControl = hasAnyFiles || !role.isSuperAdmin();
    const sharedTooltip = translate(
        role.isCustomer() ? 'attachments.tooltip.shared.customer' : 'attachments.tooltip.shared.supplier'
    );
    const internalTooltip = translate(
        role.isSuperAdmin() ? 'attachments.tooltip.internal.admin' : 'attachments.tooltip.internal'
    );

    const addFilesToUpload = (files: File[]) => {
        dispatch({
            type: 'ADD_FILES',
            files,
        });
    };

    const updateFilesInUpload = (uploadResponse: FileMessage, fileId: string) => {
        dispatch({
            type: 'ADD_UPLOAD_RESPONSE',
            uploadResponse,
            fileId,
        });
    };

    const deleteFile = async (fileId: string) => {
        dispatch({
            type: 'DELETE_FILE',
            fileId,
        });
    };

    const attachmentsConfig: AttachmentsConfig = {
        ...config,
        isInternalView,
    };

    if (!config.hasWriteRights) {
        return (
            <AttachmentsConfigContext.Provider value={attachmentsConfig}>
                {shouldShowSegmentedControl ? (
                    <Box marginBottom={16}>
                        <SegmentedControl<FileVisibility>
                            options={[
                                {
                                    value: 'shared',
                                    label: translate('attachments.shared'),
                                    count: sharedCount,
                                    tooltipLabel: sharedTooltip,
                                },
                                {
                                    value: 'internal',
                                    label: translate('attachments.internal'),
                                    count: internalCount,
                                    tooltipLabel: internalTooltip,
                                },
                            ]}
                            value={selectedVisibility}
                            onChange={setSelectedVisibility}
                        />
                    </Box>
                ) : null}
                <AttachmentList
                    hasWriteRights={false}
                    files={currentFiles}
                    companyId={companyId}
                    counterCompanyName={counterCompanyName}
                />
            </AttachmentsConfigContext.Provider>
        );
    }

    const uploadedFileIds = uploadedFiles
        .map((uploadedFile) => uploadedFile.uploadResponse?.id)
        .filter(Boolean) as string[];

    const filesToShowAsUploaded = currentFiles.filter((file) => !uploadedFileIds.includes(file.id));

    return (
        <AttachmentsConfigContext.Provider value={attachmentsConfig}>
            {shouldShowSegmentedControl ? (
                <Box marginBottom={16}>
                    <SegmentedControl<FileVisibility>
                        options={[
                            {
                                value: 'shared',
                                label: translate('attachments.shared'),
                                count: sharedCount,
                                tooltipLabel: sharedTooltip,
                            },
                            {
                                value: 'internal',
                                label: translate('attachments.internal'),
                                count: internalCount,
                                tooltipLabel: internalTooltip,
                            },
                        ]}
                        value={selectedVisibility}
                        onChange={setSelectedVisibility}
                    />
                </Box>
            ) : null}

            <UploadArea
                onFilesUpload={addFilesToUpload}
                uploadedFiles={uploadedFiles.filter((file) => !file.deleted)}
                onDeleteSuccess={deleteFile}
                onUploadSuccess={updateFilesInUpload}
            />

            <AttachmentList
                hasWriteRights={config.hasWriteRights}
                files={filesToShowAsUploaded}
                companyId={companyId}
                counterCompanyName={counterCompanyName}
            />
        </AttachmentsConfigContext.Provider>
    );
};
